import { useMutation } from '@apollo/client'
import IconTrash from 'assets/images/icon-trash-active.svg'
import ButtonBase from 'components/partials/button'
import InputBase from 'components/partials/inputBase'
import { notify } from 'components/toastify'
import { AGENCY_GROUP } from 'constants/common'
import { STATUS } from 'constants/messages'
import { IAgencyGroup } from 'pages/agency-group-management/interface'
import {
  ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG,
  AgencyGroupJobTypeEnum,
  IAgencyGroupJobConfigInput,
} from 'queries/admin/agencyGroup'
import React, { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { WebhookFormValues } from '../interface'
import { useSession } from 'helpers/checkSession'

interface Props {
  currentAgencyGroup: IAgencyGroup
  refetchData: () => void
}

const AddNewForwardWebhook: React.FC<Props> = ({ currentAgencyGroup, refetchData }) => {
  const { handleErrorSession } = useSession()
  const [isEditMode, setIsEditMode] = useState(false)

  const { control, getValues, reset, setError, clearErrors } = useForm<WebhookFormValues>({
    defaultValues: {
      url: '',
      headers: Object.entries(AGENCY_GROUP.FORWARD_WEBHOOK.DEFAULT_HEADERS).map(([key, value]) => ({
        key,
        value,
      })),
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'headers',
  })

  const [addOrEditAgencyGroupJobConfig] = useMutation(ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG)

  const handleSave = () => {
    const data = getValues()
    const keys = []
    let hasEmptyHeader = false
    let hasEmptyUrl = false
    let emptyIndex = 0

    for (let i = 0; i < data.headers.length; i++) {
      if (data.headers[i].key) {
        keys.push(data.headers[i].key.trim())
      } else {
        hasEmptyHeader = true
        emptyIndex = i
        break
      }
    }

    if (hasEmptyHeader) {
      setError(`headers.${emptyIndex}.key`, {
        type: 'manual',
        message: 'Header key cannot be empty',
      })
    } else {
      clearErrors('headers')
    }

    if (data.url.length === 0) {
      hasEmptyUrl = true
      setError(`url`, {
        type: 'manual',
        message: 'URL is required',
      })
    } else {
      clearErrors('url')
    }

    if (hasEmptyUrl || hasEmptyHeader) return

    //  Get dup keys
    const keyCount: Record<string, number[]> = {}
    keys.forEach((key, index) => {
      if (!keyCount[key]) keyCount[key] = []
      keyCount[key].push(index)
    })

    const duplicatedKeys = Object.entries(keyCount).filter(([, indexes]) => indexes.length > 1)

    if (duplicatedKeys.length > 0) {
      // Set error to the duplicate expect the first one
      duplicatedKeys.forEach(([, indexes]) => {
        indexes.slice(1).forEach((dupIndex) => {
          setError(`headers.${dupIndex}.key`, {
            type: 'manual',
            message: 'Duplicate key',
          })
        })
      })

      return
    }

    // Convert headers array to object
    const headersObject = data.headers.reduce((acc, item) => {
      if (item.key) acc[item.key] = item.value
      return acc
    }, {} as Record<string, string>)

    const finalData = {
      ...data,
      headers: headersObject,
    }

    const customConfig: IAgencyGroupJobConfigInput = {
      emailTemplateURL: null,
      htmlTemplateString: null,
      subEmailManagementConfigId: null,
      customEmailType: null,
      customJobType: AgencyGroupJobTypeEnum.FORWARD_WEBHOOK,
      forwardWebhook: finalData,
      forwardEmail: null,
      forwardFormatTemplate: null,
    }

    addOrEditAgencyGroupJobConfig({
      variables: {
        data: {
          agencyGroupId: currentAgencyGroup.agencyGroupId,
          customJobType: AgencyGroupJobTypeEnum.FORWARD_WEBHOOK,
          customConfigs: [customConfig],
        },
      },
      onCompleted: () => {
        setIsEditMode(false)
        refetchData()
        notify('Forward webhook configuration updated successfully!', STATUS.SUCCESS)
      },
      onError: (error) => {
        if (error.message) {
          handleErrorSession(error, error.message)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      },
    })

    clearErrors()
  }

  const handleAddMore = () => {
    const data = getValues()
    const hasEmptyValue = data.headers.find((item) => item.key === '')
    if (hasEmptyValue) {
      setError(`headers.${data.headers.length}.key`, {
        type: 'manual',
        message: 'Empty value',
      })
    } else {
      clearErrors()
      append({ key: '', value: '' })
    }
  }

  const handleCancel = () => {
    reset()
    setIsEditMode(false)
  }

  const onClickAddBtn = () => {
    setIsEditMode(true)
  }

  useEffect(() => {
    reset()
  }, [])

  return (
    <div>
      {currentAgencyGroup && (
        <div className="flex justify-end">
          {isEditMode ? (
            <div className="space-x-1">
              <ButtonBase onClick={handleSave}>Save</ButtonBase>
              <ButtonBase onClick={handleCancel}>Cancel</ButtonBase>
            </div>
          ) : (
            <ButtonBase onClick={onClickAddBtn}>Add</ButtonBase>
          )}
        </div>
      )}

      {isEditMode && (
        <form>
          {/**URL */}
          <div className="flex flex-col">
            <span>
              <strong>Webhook URL</strong>
            </span>
            <Controller
              name="url"
              control={control}
              render={({ field, fieldState }) => (
                <div className="w-full">
                  <InputBase className="w-full" {...field} disable={!isEditMode} />
                  {fieldState.error && (
                    <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                  )}
                </div>
              )}
            />
          </div>

          {/**Header */}
          <div className="flex flex-col pt-5">
            <span>
              <strong>Header</strong>
            </span>
            {fields?.length > 0 ? (
              <div className="space-y-2">
                {fields?.map((field, index) => (
                  <div key={field.id} className="flex w-full space-x-2">
                    <Controller
                      name={`headers.${index}.key`}
                      control={control}
                      defaultValue={field.key}
                      render={({ field, fieldState }) => (
                        <div className="w-[20%]">
                          <InputBase className="w-full" {...field} disable={!isEditMode} />
                          {fieldState.error && (
                            <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                          )}
                        </div>
                      )}
                    />

                    <Controller
                      name={`headers.${index}.value`}
                      control={control}
                      defaultValue={field.value}
                      render={({ field }) => (
                        <InputBase className="w-[85%]" {...field} disable={!isEditMode} />
                      )}
                    />
                    {isEditMode && (
                      <img
                        src={IconTrash}
                        alt="iconTrash"
                        className="cursor-pointer"
                        onClick={() => remove(index)}
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="pt-5 pb-3 text-center text-body1 text-neutral-4">No header data.</div>
            )}
            <div className="pt-5">
              <ButtonBase onClick={handleAddMore}>Add More Header</ButtonBase>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}

export default AddNewForwardWebhook
